import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Product } from '../../types/product';
import {
  sendToQuickbooks,
  getPurchaseOrderById,
  updatePurchaseOrder,
  getProducts,
  updateHandlingUnitStage,
  updateHandlingUnitLocation,
  updateHandlingUnitTruckNumber,
  updateHandlingUnitTruckDateTime,
  updateHandlingUnitReceivedCount,
  updateHandlingUnitNotes,
  addComment,
  downloadPurchaseOrderPdf,
  clearReview
} from '../../api';
import { HandlingUnitStage, HandlingUnit, UpdatePurchaseOrder, UpdatePurchaseOrderItem, PurchaseOrderHistoryEntry, PurchaseOrder, PurchaseOrderStatus } from '../../types/purchaseOrder';
import { UserRole } from '../../types/user';
import CommentsSection from '../CommentsSection';
import PurchaseOrderHeader from './PurchaseOrderHeader';
import PurchaseOrderItems from './PurchaseOrderItems';
import PurchaseOrderHistory from './PurchaseOrderHistory';
import { useAuth } from '../../contexts/AuthContext';
import { 
  EditPurchaseOrderDialog,
  EditableHandlingUnit, 
  EditablePurchaseOrderItem, 
  FormState,
  convertToPurchaseOrderItem,
  convertToFormState,
  isExistingHandlingUnit,
  isHandlingUnitNumberUnique,
  convertHandlingUnitForUpdate
} from './edit-dialog';

const PurchaseOrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const { user, role } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sendingToQuickbooks, setSendingToQuickbooks] = useState(false);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<FormState>>({});
  const [newHU, setNewHU] = useState<{ huNumber?: string }>({});
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [comments, setComments] = useState<PurchaseOrderHistoryEntry[]>([]);
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(null);

  // Monitor form data changes
  useEffect(() => {
    console.log('editFormData changed:', {
      formData: editFormData,
      items: editFormData.items?.map((item: EditablePurchaseOrderItem) => ({
        itemNumber: item.itemNumber,
        handlingUnits: item.handlingUnits.map((hu: EditableHandlingUnit) => hu.huNumber),
        hu: item.hu
      }))
    });
  }, [editFormData]);

  const addNotification = (severity: 'success' | 'error', detail: string) => {
    toast.current?.show({
      severity,
      summary: severity === 'success' ? 'Success' : 'Error',
      detail,
      life: 3000
    });
  };

  const loadPurchaseOrder = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await getPurchaseOrderById(parseInt(id));
      setPurchaseOrder(response);
      const formData = convertToFormState(response);
      setEditFormData(formData);
      setComments(response.history || []);
    } catch (error) {
      console.error('Error loading purchase order:', error);
      addNotification('error', 'Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPurchaseOrder();
  }, [id]);

  const handleSearchProducts = async (event: { query: string }) => {
    try {
      const response = await getProducts({
        search: event.query,
        page: 1,
        limit: 10,
        sortBy: 'name',
        sortOrder: 'asc'
      });
      setFilteredProducts(response.data);
    } catch (error) {
      console.error('Error searching products:', error);
      addNotification('error', 'Failed to search products');
    }
  };

  const handleAddComment = async (comment: string) => {
    if (!id || !user) return;
    try {
      await addComment(parseInt(id), comment);
      addNotification('success', 'Comment added successfully');
      loadPurchaseOrder(); // Reload to get updated comments
    } catch (error) {
      console.error('Error adding comment:', error);
      addNotification('error', 'Failed to add comment');
    }
  };

  const handleDownloadPdf = async () => {
    if (!id) return;
    try {
      await downloadPurchaseOrderPdf(parseInt(id));
      addNotification('success', 'PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      addNotification('error', 'Failed to download PDF');
    }
  };

  const handleSendToQuickbooks = async () => {
    if (!id) return;
    try {
      setSendingToQuickbooks(true);
      await sendToQuickbooks(parseInt(id));
      addNotification('success', 'Purchase order sent to QuickBooks successfully');
    } catch (error) {
      console.error('Error sending to QuickBooks:', error);
      addNotification('error', 'Failed to send purchase order to QuickBooks');
    } finally {
      setSendingToQuickbooks(false);
    }
  };

  const handleUpdateHandlingUnitStage = async (huNumber: string, stage: HandlingUnitStage) => {
    if (!user) return;
    try {
      await updateHandlingUnitStage(huNumber, stage, user.username);
      addNotification('success', 'Stage updated successfully');
      loadPurchaseOrder();
    } catch (error) {
      console.error('Error updating stage:', error);
      addNotification('error', 'Failed to update stage');
    }
  };

  const handleUpdateLocation = async (huNumber: string, location: string) => {
    if (!user) return;
    try {
      await updateHandlingUnitLocation(huNumber, location, user.username);
      addNotification('success', 'Location updated successfully');
      loadPurchaseOrder();
    } catch (error) {
      console.error('Error updating location:', error);
      addNotification('error', 'Failed to update location');
    }
  };

  const handleUpdateTruckNumber = async (huNumber: string, truckNumber: number | null) => {
    if (!user) return;
    try {
      await updateHandlingUnitTruckNumber(huNumber, truckNumber, user.username);
      addNotification('success', 'Truck number updated successfully');
      loadPurchaseOrder();
    } catch (error) {
      console.error('Error updating truck number:', error);
      addNotification('error', 'Failed to update truck number');
    }
  };

  const handleUpdateTruckDateTime = async (huNumber: string, date: Date | null) => {
    if (!user) return;
    try {
      await updateHandlingUnitTruckDateTime(huNumber, date, user.username);
      addNotification('success', 'Truck date/time updated successfully');
      loadPurchaseOrder();
    } catch (error) {
      console.error('Error updating truck date/time:', error);
      addNotification('error', 'Failed to update truck date/time');
    }
  };

  const handleUpdateReceivedCount = async (huNumber: string, receivedCount: number) => {
    if (!user) return;
    try {
      await updateHandlingUnitReceivedCount(huNumber, receivedCount, user.username);
      addNotification('success', 'Received count updated successfully');
      loadPurchaseOrder();
    } catch (error) {
      console.error('Error updating received count:', error);
      addNotification('error', 'Failed to update received count');
    }
  };

  const handleUpdateHandlingUnitNote = async (huNumber: string, note: string) => {
    try {
      await updateHandlingUnitNotes(huNumber, note);
      addNotification('success', 'Note updated successfully');
      await loadPurchaseOrder();
    } catch (error) {
      console.error('Error updating handling unit note:', error);
      addNotification('error', 'Failed to update note');
    }
  };

  const handleClearReview = async () => {
    if (!id) return;
    try {
      await clearReview(parseInt(id));
      addNotification('success', 'Purchase order marked as reviewed');
      loadPurchaseOrder();
    } catch (error) {
      console.error('Error clearing review flag:', error);
      addNotification('error', 'Failed to mark purchase order as reviewed');
    }
  };

  const handleEditSubmit = async () => {
    if (!editFormData.poNumber?.trim()) {
      addNotification('error', 'PO Number is required');
      return;
    }

    if (!editFormData.items?.length) {
      addNotification('error', 'At least one item is required');
      return;
    }

    const toYmd = (d: string | Date | undefined): string | undefined => {
      if (!d) return undefined;
      const date = d instanceof Date ? d : new Date(d);
      return isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10);
    };

    try {
      const transformedItems = editFormData.items.map((item: EditablePurchaseOrderItem) => {
        const convertedItem = convertToPurchaseOrderItem(item);
        const allHuNumbers = item.handlingUnits.map((hu: EditableHandlingUnit) => hu.huNumber);
        const defaultDate = new Date().toISOString().split('T')[0];
        const itemDeliveryDate = toYmd(convertedItem.deliveryDate) || defaultDate;

        const handlingUnits = item.handlingUnits.map((hu: EditableHandlingUnit) => {
          const baseHu = {
            id: 0,
            huNumber: hu.huNumber,
            stage: hu.stage || HandlingUnitStage.Pending,
            deliveryDate: toYmd(hu.deliveryDate) || itemDeliveryDate,
            location: hu.location,
            receivedCount: hu.receivedCount,
            notes: hu.notes,
            purchaseOrderItemId: isExistingHandlingUnit(hu) ? hu.purchaseOrderItemId : convertedItem.id
          };

          if (isExistingHandlingUnit(hu)) {
            baseHu.id = hu.id;
          }

          return baseHu;
        });

        return {
          id: convertedItem.id,
          itemNumber: convertedItem.itemNumber,
          material: item.material || '',
          productId: item.productId,
          quantity: convertedItem.quantity,
          unitPrice: convertedItem.unitPrice,
          unit: convertedItem.unit,
          currency: convertedItem.currency,
          shipToParty: convertedItem.shipToParty,
          batchNumber: convertedItem.batchNumber,
          stage: convertedItem.stage || HandlingUnitStage.Pending,
          deliveryDate: itemDeliveryDate,
          handlingUnits,
          hu: allHuNumbers,
          replaceHandlingUnits: true
        };
      });

      const updateData: UpdatePurchaseOrder = {
        poNumber: editFormData.poNumber,
        notes: editFormData.notes,
        items: transformedItems,
        replaceItems: true
      };

      await updatePurchaseOrder(parseInt(id!), updateData);
      addNotification('success', 'Purchase order updated successfully');
      setEditDialogVisible(false);
      loadPurchaseOrder();
    } catch (error) {
      console.error('Error updating purchase order:', error);
      addNotification('error', 'Failed to update purchase order');
    }
  };

  const removeHandlingUnit = (itemIndex: number, huIndex: number) => {
    setEditFormData((prevFormData: Partial<FormState>) => {
      const updatedItems = [...(prevFormData.items || [])] as EditablePurchaseOrderItem[];
      const item = updatedItems[itemIndex];
      
      // Remove handling unit from both arrays
      const updatedHandlingUnits = item.handlingUnits.filter((_: HandlingUnit, index: number) => index !== huIndex);
      const updatedHuNumbers = updatedHandlingUnits.map(hu => hu.huNumber);
      
      updatedItems[itemIndex] = {
        ...item,
        handlingUnits: updatedHandlingUnits,
        hu: updatedHuNumbers
      };

      return {
        ...prevFormData,
        items: updatedItems
      };
    });
  };

  const handleAddHU = (itemIndex: number) => {
    if (!newHU.huNumber) {
      console.error('No HU number provided');
      addNotification('error', 'Handling Unit number is required');
      return;
    }

    setEditFormData((prevFormData: Partial<FormState>) => {
      if (!prevFormData.items) {
        console.error('No items in form data');
        addNotification('error', 'Failed to add handling unit: No items found');
        return prevFormData;
      }

      const updatedItems = [...prevFormData.items];
      const item = updatedItems[itemIndex];

      if (!item) {
        console.error('Item not found at index:', itemIndex);
        addNotification('error', 'Failed to add handling unit: Item not found');
        return prevFormData;
      }

      const huNumber = newHU.huNumber!;

      // Create new handling unit with required fields
      const newHandlingUnit: HandlingUnit = {
        id: 0, // New handling units get ID 0 until saved
        huNumber,
        stage: HandlingUnitStage.Pending,
        deliveryDate: item.deliveryDate,
        purchaseOrderItemId: item.id || 0,
        location: undefined,
        receivedCount: undefined,
        handlingUnitOutboundTruckNumber: undefined,
        huTruckDateTime: undefined,
        history: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Add new handling unit
      const updatedHandlingUnits = [...item.handlingUnits, newHandlingUnit];
      const updatedHuNumbers = updatedHandlingUnits.map(hu => hu.huNumber);

      updatedItems[itemIndex] = {
        ...item,
        handlingUnits: updatedHandlingUnits,
        hu: updatedHuNumbers
      };

      // Clear the new HU input
      setNewHU({});

      return {
        ...prevFormData,
        items: updatedItems
      };
    });
  };

  if (loading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <ProgressSpinner />
      </div>
    );
  }

  if (!purchaseOrder) {
    return (
      <div className="p-4">
        <div className="text-xl">Purchase order not found</div>
      </div>
    );
  }

  const canUpdateStage = role ? [UserRole.Admin, UserRole.Manager, UserRole.Warehouse].includes(role) : false;
  const canSetTruckNumber = role ? [UserRole.Admin, UserRole.Manager, UserRole.Warehouse].includes(role) : false;

  return (
    <div className="p-4">
      <Toast ref={toast} />
      
      <div className="flex justify-content-between align-items-center mb-4">
        <h1 className="text-2xl">Purchase Order Details</h1>
        <div>
          <Button 
            label="Send to QuickBooks" 
            icon="pi pi-send" 
            onClick={handleSendToQuickbooks}
            disabled={sendingToQuickbooks}
            className="mr-2 p-button-success"
          />
          <Button 
            label="Create Ticket" 
            icon="pi pi-ticket" 
            onClick={() => navigate(`/tickets/create?poId=${purchaseOrder.id}`)}
            className="mr-2 p-button-info"
          />
          <Button 
            label="Edit" 
            icon="pi pi-pencil" 
            onClick={() => setEditDialogVisible(true)}
            className="mr-2"
          />
          <Button 
            label="Back" 
            icon="pi pi-arrow-left" 
            onClick={() => navigate('/purchase-orders')}
            className="p-button-secondary"
          />
        </div>
      </div>

      {purchaseOrder.needsReview && (
        <div className="mb-4">
          <Message
            severity="warn"
            className="w-full justify-content-start"
            content={
              <div className="flex align-items-center justify-content-between w-full">
                <span>
                  <strong>Needs review:</strong> {purchaseOrder.reviewReason || 'This purchase order was flagged for review.'}
                </span>
                <Button
                  label="Mark reviewed"
                  icon="pi pi-check"
                  className="p-button-sm p-button-warning ml-3"
                  onClick={handleClearReview}
                />
              </div>
            }
          />
        </div>
      )}

      <div className="grid">
        <div className="col-12">
          <PurchaseOrderHeader
            purchaseOrder={purchaseOrder} 
            onDownloadPdf={handleDownloadPdf}
          />
        </div>

        <div className="col-12">
          <PurchaseOrderItems 
            purchaseOrder={purchaseOrder}
            canUpdateStage={canUpdateStage}
            canSetTruckNumber={canSetTruckNumber}
            username={user?.username || ''}
            onUpdateHandlingUnitStage={handleUpdateHandlingUnitStage}
            onUpdateTruckNumber={handleUpdateTruckNumber}
            onUpdateTruckDateTime={handleUpdateTruckDateTime}
            onUpdateLocation={handleUpdateLocation}
            onUpdateReceivedCount={handleUpdateReceivedCount}
            onUpdateHandlingUnitNote={handleUpdateHandlingUnitNote}
          />
        </div>

        <div className="col-12">
          <PurchaseOrderHistory 
            purchaseOrder={purchaseOrder}
            onAddComment={handleAddComment}
          />
        </div>

        <div className="col-12">
          <CommentsSection 
            comments={comments}
            onAddComment={handleAddComment}
          />
        </div>
      </div>

      <EditPurchaseOrderDialog
        visible={editDialogVisible}
        onHide={() => setEditDialogVisible(false)}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        handleEditSubmit={handleEditSubmit}
        handleRemoveHU={removeHandlingUnit}
        handleAddHU={handleAddHU}
        newHU={newHU}
        setNewHU={setNewHU}
        filteredProducts={filteredProducts}
        searchProducts={handleSearchProducts}
      />
    </div>
  );
};

export default PurchaseOrderDetails;
